import React, { Suspense, useEffect, useState, useMemo, lazy } from "react";
import { Badge, Button, Col, Image, Radio, Row, Tooltip } from "antd";
import { SettingOutlined } from "@ant-design/icons";
import { nurseCareItemsList } from "../../Options/careItemsOptions";
import {
  isNurseAddedCareItem,
  nurseCareItemKey,
} from "./careItemData";
import { getNurseCommonData } from "../../../../services/nurse.services";
import DailyRoutine from "./DailyRoutine/DailyRoutine";
import useQuery from "../../../../hooks/useQuery";
import dayjs from "dayjs";
import LoadingIndicator from "../../../Loaders/LoadingIndicator";
import SosCareItemsModal from "./SosCareItemsModal";
import {
  makeSosPlaceholderKey,
  registerSosPlaceholder,
} from "./sosPlaceholders";
import "./CareItemsMobile.css";
import "./CareItemsTable.css";
// import { getBadgeColor } from "../../../../helpers/getBadgeColor";
// Dynamic import map
const componentMap = {
  nebulization: lazy(() => import("./Nebulisation")),
  iv_fluids: lazy(() => import("./IvFluids")),
  blood_products: lazy(() => import("./BloodProducts")),
  parental_nutrition: lazy(() => import("./ParentalNutrition")),
  respiratory_care: lazy(() => import("./RespiratoryCare/RespiratoryCare")),
  urinary_catheter_care: lazy(() => import("./UrinaryCatheter")),
  open_wound_care: lazy(() => import("./WoundCare/WoundCare")),
  preventive_care: lazy(() => import("./PreventiveCare/PreventiveCare")),
  eye_care: lazy(() => import("./EyeCare/EyeCare")),
  feeding_tube: lazy(() => import("./FeedingTube")),
  pressure_sore: lazy(() => import("./PressureSore/PressureSore")),
  ostomy_care: lazy(() => import("./Ostomy")),
  position_change_schedule: lazy(() => import("./PositionChange")),
  specialist_consultation: lazy(() => import("./SpecialistConsultation")),
};
const dateFormat = "YYYY-MM-DD";

/*
 * A care item the nurse turned on for an SOS arrives with no action rows, and
 * every care item component draws its table - and the SOS button with it -
 * once per action. So an item the nurse had just added showed as an empty
 * tab with no way to record anything against it.
 *
 * One empty row is stood in for it here, in the one place the day's care is
 * read, so every care type gets the button without each component having to
 * know about it. The row carries no id: there is no action on the chart yet,
 * and SosModal refuses to save against a row that has none.
 */
/*
 * The same care item under two names.
 *
 * Enabling files the record under the care type the nurse picked, while what
 * is charted against it comes back under the care type the order sheet uses:
 *
 *   "pressure_sore":      [{ actions: [], enabled_care_item_id: 145 }]
 *   "pressure_sore_care": [{ pressure_sore_dressing: { nurse_data: [...] } }]
 *
 * The tab reads the first, so a recorded SOS sat in the response unread. The
 * two lists are put together before anything else looks at them.
 */
const CARE_TYPE_ALIASES = { pressure_sore: "pressure_sore_care" };

const mergeAliasedCareTypes = (data) => {
  const merged = { ...data };
  Object.entries(CARE_TYPE_ALIASES).forEach(([key, alias]) => {
    if (!Array.isArray(merged[alias])) return;
    merged[key] = [
      ...(Array.isArray(merged[key]) ? merged[key] : []),
      ...merged[alias],
    ];
    delete merged[alias];
  });
  return merged;
};

const foldNurseAddedCareItems = (list, careType) => {
  const folded = [];
  const seen = new Map();

  list.forEach((item) => {
    /* Only what the nurse added is folded - the doctor's orders stand on
       their own, several of the same care item being several orders. */
    if (!isNurseAddedCareItem(item)) {
      folded.push(item);
      return;
    }

    const name = item?.drug_name ?? "";
    if (!seen.has(name)) {
      seen.set(name, folded.length);
      folded.push({ ...item });
      return;
    }

    const at = seen.get(name);
    const base = folded[at];
    const next = { ...base };

    /* The charts this care type is recorded on are named after it. */
    Object.keys(item).forEach((field) => {
      if (field.startsWith(`${careType}_`) && !next[field]) {
        next[field] = item[field];
      }
    });
    next.actions = [
      ...(Array.isArray(base?.actions) ? base.actions : []),
      ...(Array.isArray(item?.actions) ? item.actions : []),
    ];
    /* Whichever of them carries the enable is what an SOS is recorded
       against, so it is kept. */
    next.enabled_care_item_id =
      base?.enabled_care_item_id ?? item?.enabled_care_item_id;
    folded[at] = next;
  });

  return folded;
};

const withSosPlaceholder = (rawData, admissionId) => {
  if (!rawData || typeof rawData !== "object") return rawData;
  const data = mergeAliasedCareTypes(rawData);
  const out = {};
  Object.entries(data).forEach(([key, value]) => {
    if (!Array.isArray(value)) {
      out[key] = value;
      return;
    }
    /*
     * The SOS endpoint files each recording as a care item of its own, so one
     * care item the nurse added comes back several times over: once for the
     * enable, carrying nothing, and once more for every SOS, each carrying
     * only the chart it was recorded on. Drawn as they arrive that is a card
     * apiece - an empty one, then one per chart.
     *
     * They are folded back into the one care item they describe, so a nurse
     * added care item is a single card carrying all of its charts.
     */
    const list = foldNurseAddedCareItems(value, key);

    out[key] = list.map((item) => {
      const nurseKey = nurseCareItemKey(item);
      if (nurseKey === undefined) return item;

      /* The key stands in for an action id. SosModal recognises it and
         creates the action through the SOS endpoint instead of patching one
         that does not exist. */
      const placeholderKey = makeSosPlaceholderKey(nurseKey);
      registerSosPlaceholder(placeholderKey, {
        admissionId,
        care_item_type: key,
        drug_name: item?.drug_name || "",
        data: item?.data || {},
      });

      const hasActions = Array.isArray(item?.actions) && item.actions.length;
      if (hasActions) {
        /* Once the first SOS has been recorded the stand-in is no longer
           needed, but the rows carry the key so the table can still offer
           the button - otherwise it vanished after the first entry and a
           second SOS could not be recorded.
           They carry it rather than their own id because a second SOS is a
           second round of care: it is recorded as its own action, which is
           what the charts here list. Added to the first action instead, it
           went into a field these tables do not draw - so only the first SOS
           ever appeared. */
        return {
          ...item,
          actions: item.actions.map((action) => ({
            ...action,
            enabled_care_item_id: nurseKey,
            sos_placeholder_key: placeholderKey,
          })),
        };
      }
      return {
        ...item,
        actions: [
          {
            id: placeholderKey,
            nurse_data: [],
            is_stopped: false,
            awaiting_first_action: true,
          },
        ],
      };
    });
  });
  return out;
};
function CareItemTabs({ isSpecialArea = false }) {
  const curDate = dayjs().format(dateFormat);
  const query = useQuery();
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({ daily_routine_care: [{}] });
  const [activeRadio, setActiveRadio] = useState(
    query?.care || "daily_routine_care"
  );
  const [dailyData, setDailyData] = useState({});
  const [sosOpen, setSosOpen] = useState(false);
  async function getData() {
    try {
      const response = await getNurseCommonData(
        query?.patient,
        `care_items`,
        curDate
      );

      setFormData({
        ...withSosPlaceholder(response?.data?.data, query?.patient),
        daily_routine_care: [{}],
      });
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  }

  async function getdailyCareData() {
    try {
      const response = await getNurseCommonData(
        query?.patient,
        "daily_routine_care",
        curDate
      );

      setDailyData(response?.data?.data);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (query?.patient) getData();
    {
      activeRadio === "daily_routine_care" && getdailyCareData();
    }
  }, [query?.patient, activeRadio]);

  const ActiveComponent = useMemo(
    () => componentMap[activeRadio] || null,
    [activeRadio]
  );

  useEffect(() => {
    if (!loading) {
      // Check if the current activeRadio is valid or needs to be updated
      const availableKeys = [
        ...nurseCareItemsList,
        { label: "Daily Routine", key: "daily_routine_care" },
      ]
        .map(({ key }) => key)
        .filter((key) =>
          Object.keys(formData).some((item) => item.startsWith(key))
        );

      // If the current activeRadio is not valid, update it to the first available key
      if (!availableKeys.includes(activeRadio) && availableKeys.length > 0) {
        setActiveRadio(availableKeys[0]);
      }
    }
  }, [formData, loading]); // Re-run when formData or activeRadio changes

  function imageLoader(name) {
    try {
      return name === activeRadio
        ? require(`../../../../assets/careItemIcons/${name}_light.png`)
        : require(`../../../../assets/careItemIcons/${name}.png`);
    } catch (error) {
      console.error(`Image not found: ${name}`, error);
      return null;
    }
  }

  const getFormDataValue = () => {
    if (formData[activeRadio]) return formData[activeRadio];

    if (Object.keys(formData).some((item) => item.startsWith(activeRadio)))
      return formData;

    return undefined;
  };

  const getBadgeColor = (key) => {
    if (["iv_fluids", "blood_products", "parental_nutrition"].includes(key)) {
      const items = formData[key];
      return items &&
        Array.isArray(items) &&
        items.every((item) =>
          item.actions?.some((action) => action?.is_stopped === true)
        )
        ? "red"
        : "green";
    }

    const activeItems = Object.keys(formData).filter((dataKey) =>
      dataKey.startsWith(key)
    );

    for (const dataKey of activeItems) {
      const items = formData[dataKey];

      if (items && Array.isArray(items)) {
        for (const element of items) {
          if (element?.actions) {
            const isActive = element.actions.some(
              (action) => action?.is_stopped === false
            );
            if (isActive) {
              return "green";
            }
          } else {
            const baseKey = key.split("_").slice(0, 2).join("_"); // Get the base part of the key
            const nestedKeys = Object.keys(element).filter(
              (prop) => prop.startsWith(baseKey) // Check for keys that start with the base key
            );

            for (const nestedKey of nestedKeys) {
              if (element[nestedKey]?.is_stopped === false) {
                return "green";
              }
            }
          }
        }
      } else {
        return "red";
      }
    }
    return "red";
  };

  return (
    <Row className="nurse-care-items-tabs">
      <Col span={24}>
        <div className="care-items-tab-bar">
        <Radio.Group
          className="care-items-tab-group"
          onChange={(e) => {
            setActiveRadio(e.target.value);
          }}
          buttonStyle="solid"
          value={activeRadio} // Keep this to bind with activeRadio state
        >
          {!loading && (
            <Radio.Button value="daily_routine_care" key="daily_routine_care">
              <span className="care-item-tab-inner">
                <span className="care-item-tab-text" style={{ fontSize: "12px" }}>
                  Daily Routine
                </span>
                <Image
                  preview={false}
                  src={imageLoader("daily_routine_care")}
                  className="care-item-tab-icon"
                  style={{ height: "18px", width: "auto" }}
                />
              </span>
              <Badge
                color="green"
                status="processing"
                className="custom-badge-size care-item-tab-badge"
              />
            </Radio.Button>
          )}
          {nurseCareItemsList.map(
            ({ label, key }) =>
              Object.keys(formData).some((item) => item.startsWith(key)) && (
                <Radio.Button
                  key={key}
                  value={key}
                  style={{ marginBottom: "10px" }}
                >
                  <span className="care-item-tab-inner">
                    <span
                      className="care-item-tab-text"
                      style={{ fontSize: "12px" }}
                    >
                      {label}
                    </span>
                    <Image
                      preview={false}
                      src={imageLoader(key)}
                      className="care-item-tab-icon"
                      style={{ height: "20px", width: "auto" }}
                    />
                  </span>
                  <Badge
                    color={getBadgeColor(key)}
                    status="processing"
                    className="custom-badge-size care-item-tab-badge"
                  />
                </Radio.Button>
              )
          )}
        </Radio.Group>
        {/* Care items the doctor has not ordered have no tab, so this is the
            way in to record one given as SOS. */}
        <Tooltip title="Record SOS care item">
          <Button
            type="text"
            className="care-items-sos-gear"
            aria-label="Record SOS care item"
            icon={<SettingOutlined />}
            onClick={() => setSosOpen(true)}
          />
        </Tooltip>
        </div>
      </Col>
      <Col span={24}>
        {activeRadio === "daily_routine_care" ? (
          <DailyRoutine formData={dailyData} getData={getdailyCareData} />
        ) : (
          <Suspense fallback={<LoadingIndicator message={"Loading..."} />}>
            {!loading && ActiveComponent && (
              <ActiveComponent
                formData={getFormDataValue()}
                getData={getData}
                isSpecialArea={isSpecialArea}
                activeRadio={activeRadio}
              />
            )}
          </Suspense>
        )}
      </Col>
      {sosOpen && (
        <SosCareItemsModal
          open={sosOpen}
          setOpen={setSosOpen}
          formData={formData}
          admissionId={query?.patient}
          getData={getData}
        />
      )}
    </Row>
  );
}

export default CareItemTabs;
